import { g } from "../globalState/g";
import { a, getSelectedShapes } from '../globalState/a';

import { mat3 } from "gl-matrix";

import { Point } from "../../models/Point";
import { AbstractFrame } from "../../models/frames/AbstractFrame";



import { arrayFirst } from "../../utils/exstensions";
import { restorePipe } from "../../services/operations";
import { ObjectTypes, DrawingModes } from "../enums";
import { resetWebGLMatrices } from "../utils/matrixHelpers";
import { Line } from "../../models/shapes/Line";

/**
 * Get size string from a Line's network info
 * Returns formatted size string or 'UNKNOWN' if size is not assigned
 */
function getDuctSizeString(line: Line): string {
    if (!line.net) {
        return 'UNKNOWN';
    }

    const net = line.net;
    
    // Check for rectangular duct (width and height)
    if (net.widthMil && net.heightMil) {
        return `${net.widthMil}x${net.heightMil}`;
    }
    
    // Check for round duct (diameter)
    if (net.diameterMil) {
        return `${net.diameterMil}`;
    }
    
    return 'UNKNOWN';
}

// Store previous label position to clear it
let previousLabelBounds: { x: number; y: number; width: number; height: number } | null = null;

/**
 * Draw size labels for selected Line segments in duct mode
 * Only shows label when mouse is hovering over a selected line
 * Uses the text canvas overlay to display size information
 */
function drawDuctSizeLabels() {
    // Only draw in duct mode
    if (a.drawingMode !== DrawingModes.duct) {
        // Clear previous label if we're not in duct mode
        if (previousLabelBounds) {
            const textCanvas = document.querySelector('.text') as HTMLCanvasElement;
            if (textCanvas) {
                const ctx = textCanvas.getContext('2d');
                if (ctx) {
                    const shadowPadding = 8; // Extra padding for shadow
                    ctx.clearRect(
                        previousLabelBounds.x - shadowPadding,
                        previousLabelBounds.y - shadowPadding,
                        previousLabelBounds.width + shadowPadding * 2,
                        previousLabelBounds.height + shadowPadding * 2
                    );
                }
            }
            previousLabelBounds = null;
        }
        return;
    }

    // Check if mouse position is available
    if (!a.mouse) {
        return;
    }

    // Get text canvas context
    const textCanvas = document.querySelector('.text') as HTMLCanvasElement;
    if (!textCanvas) {
        return;
    }

    const ctx = textCanvas.getContext('2d');
    if (!ctx) {
        return;
    }

    // Get selected Line segments
    const selectedLines = getSelectedShapes().filter(shape => shape instanceof Line && shape.isSelected) as Line[];
    
    if (selectedLines.length === 0) {
        // Clear previous label if no selected lines
        if (previousLabelBounds) {
            const shadowPadding = 8;
            ctx.clearRect(
                previousLabelBounds.x - shadowPadding,
                previousLabelBounds.y - shadowPadding,
                previousLabelBounds.width + shadowPadding * 2,
                previousLabelBounds.height + shadowPadding * 2
            );
            previousLabelBounds = null;
        }
        return;
    }

    // Find the selected line that the mouse is hovering over
    const hoveredLine = selectedLines.find(line => line.isinSelectBoundary && line.isinSelectBoundary(a.mouse));
    
    if (!hoveredLine) {
        // Clear previous label if mouse is not over any selected line
        if (previousLabelBounds) {
            const shadowPadding = 8;
            ctx.clearRect(
                previousLabelBounds.x - shadowPadding,
                previousLabelBounds.y - shadowPadding,
                previousLabelBounds.width + shadowPadding * 2,
                previousLabelBounds.height + shadowPadding * 2
            );
            previousLabelBounds = null;
        }
        return;
    }

    const sizeText = getDuctSizeString(hoveredLine);
    
    // Calculate midpoint of the line
    const midX = (hoveredLine.start.x + hoveredLine.end.x) / 2;
    const midY = (hoveredLine.start.y + hoveredLine.end.y) / 2;
    
    // Save current canvas state
    ctx.save();
    
    // Set font properties
    ctx.font = 'bold 13px Arial';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    
    // Measure text for background sizing
    const textMetrics = ctx.measureText(sizeText);
    const paddingX = 10;
    const paddingY = 6;
    const bgWidth = textMetrics.width + paddingX * 2;
    const bgHeight = 22;
    const cornerRadius = 6;
    const shadowPadding = 8; // Extra padding for shadow
    
    const bgX = midX - bgWidth / 2;
    const bgY = midY - bgHeight / 2;
    
    // Clear previous label area if it exists and is different from current
    if (previousLabelBounds) {
        const isSamePosition = 
            Math.abs(previousLabelBounds.x - bgX) < 1 &&
            Math.abs(previousLabelBounds.y - bgY) < 1 &&
            Math.abs(previousLabelBounds.width - bgWidth) < 1 &&
            Math.abs(previousLabelBounds.height - bgHeight) < 1;
        
        if (!isSamePosition) {
            ctx.clearRect(
                previousLabelBounds.x - shadowPadding,
                previousLabelBounds.y - shadowPadding,
                previousLabelBounds.width + shadowPadding * 2,
                previousLabelBounds.height + shadowPadding * 2
            );
        }
    }
    
    // Clear current area (including shadow area) before drawing
    ctx.clearRect(
        bgX - shadowPadding,
        bgY - shadowPadding,
        bgWidth + shadowPadding * 2,
        bgHeight + shadowPadding * 2
    );
    
    // Draw shadow for depth
    ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;
    
    // Draw rounded rectangle background
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#4A90E2';
    ctx.lineWidth = 1;
    
    // Draw rounded rectangle
    ctx.beginPath();
    ctx.moveTo(bgX + cornerRadius, bgY);
    ctx.lineTo(bgX + bgWidth - cornerRadius, bgY);
    ctx.quadraticCurveTo(bgX + bgWidth, bgY, bgX + bgWidth, bgY + cornerRadius);
    ctx.lineTo(bgX + bgWidth, bgY + bgHeight - cornerRadius);
    ctx.quadraticCurveTo(bgX + bgWidth, bgY + bgHeight, bgX + bgWidth - cornerRadius, bgY + bgHeight);
    ctx.lineTo(bgX + cornerRadius, bgY + bgHeight);
    ctx.quadraticCurveTo(bgX, bgY + bgHeight, bgX, bgY + bgHeight - cornerRadius);
    ctx.lineTo(bgX, bgY + cornerRadius);
    ctx.quadraticCurveTo(bgX, bgY, bgX + cornerRadius, bgY);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    
    // Draw text with gradient-like effect (darker color for better contrast)
    ctx.fillStyle = '#2C3E50';
    ctx.fillText(sizeText, midX, midY);
    
    // Store current label bounds for next clear
    previousLabelBounds = {
        x: bgX,
        y: bgY,
        width: bgWidth,
        height: bgHeight
    };
    
    // Restore canvas state
    ctx.restore();
}

function drawShapes() {

    // это для того, чтобы фигуры раздваивались
    if (g.context && g.u_move && g.u_rotate && g.u_scale) {
        resetWebGLMatrices(g.context, {
            u_move: g.u_move,
            u_rotate: g.u_rotate,
            u_scale: g.u_scale
        });
    }

    if (g.context) {
        g.context.clearColor(1, 1, 1, 1);
        // закомментировать если где-то нужно нарисовать что-то локально, ручку например функцией drawSingle()
        g.context.clear(g.context.COLOR_BUFFER_BIT);
    }

    // 16 000 фигур 20 ms
    // a.shapes.forEach(shape => {
    //     drawSingle(shape);
    // });

    // цикл for совсем немного, но быстрее, чем forEach
    // 16 000 фигур 17 ms

    var length = a.activeShapes.length;

    for (let i = 0; i < length; i++) {
        const element = a.activeShapes[i];
        drawSingle(element);
    }
    
    // Draw duct size labels after shapes are drawn
    drawDuctSizeLabels();
}


function drawSingle(shape: unknown) {
    /**
     * Предполагается, что основное общение с webgl будет происходить через эту функцию.
     * В то же время трансформации происходят также через функцию handleMouseMove
     * @param {Line, Grip, Projection, Circle, Rectangle} shape - фигура, которую нужно отрисовать.
     * В качестве фигуры также могут выступать и магниты
     */
    const s = shape as any;

    if (s.type !== ObjectTypes.line && s.type !== ObjectTypes.cpoint) {
        var vertices = s.saved ? s.saved : s.getVertices();
    }
    else {
        var vertices = s.getVertices();
    }


    var size = vertices.length;
    const [a, b, c, d] = s.color;
    if (s.isSelected) {
        switch (s.type) {
            case ObjectTypes.line:
                s.grip.center = s.start;
                drawSingle(s.grip);
                s.grip.center = s.end;
                drawSingle(s.grip);
                break;
            case 'guide':
                s.grip.center = s.start;
                drawSingle(s.grip);
                s.grip.center = s.end;
                drawSingle(s.grip);
                break;
            case ObjectTypes.rectangle:
                s.grip.center = s.p1;
                drawSingle(s.grip);
                s.grip.center = s.p2;
                drawSingle(s.grip);
                s.grip.center = s.p3;
                drawSingle(s.grip);
                s.grip.center = s.p4;
                drawSingle(s.grip);
                break;
            case ObjectTypes.circle:
                s.grip.center = s.quad1;
                drawSingle(s.grip);
                s.grip.center = s.quad2;
                drawSingle(s.grip);
                s.grip.center = s.quad3;
                drawSingle(s.grip);
                s.grip.center = s.quad4;
                drawSingle(s.grip);
                break;
            case ObjectTypes.tee:
                const crucialPoints = s.getCrucialPoints();
                const originalGripColor = [...s.grip.color];
                
                // Draw start point in red
                if (crucialPoints.start) {
                    s.grip.center = crucialPoints.start;
                    s.grip.color = [1, 0, 0, 1]; // Red for start
                    drawSingle(s.grip);
                }
                // Draw end point in blue
                if (crucialPoints.end) {
                    s.grip.center = crucialPoints.end;
                    s.grip.color = [0, 0, 1, 1]; // Blue for end
                    drawSingle(s.grip);
                }
                // Draw mid point in yellow
                if (crucialPoints.mid) {
                    s.grip.center = crucialPoints.mid;
                    s.grip.color = [1, 1, 0, 1]; // Yellow for mid
                    drawSingle(s.grip);
                }
                
                // Restore original grip color
                s.grip.color = originalGripColor;
                break;
            default:
                break;
        }
         // Check if element belongs to calculated branch - use red color instead of gray
        if (g.context && g.u_color) {
            if ((s as any).isCalculatedBranch) {
                g.context.uniform4f(g.u_color, 1.0, 0.0, 0.0, 1); // Red for calculated branch
            } else {
                g.context.uniform4f(g.u_color, 0.5, 0.5, 0.5, 1); // Gray for normal selection
            }
        }
    }
    else {
        if (g.context && g.u_color) {
            g.context.uniform4f(g.u_color, a, b, c, d);
        }
    }

    if (!g.context) return;

    g.context.bufferData(g.context.ARRAY_BUFFER, vertices, g.context.DYNAMIC_DRAW);

    switch (s.type) {
        case 'select_frame':
            g.context.drawArrays(g.context.LINE_LOOP, 0, size / 2);

            break;
        case 'selectBoundary':
            g.context.drawArrays(g.context.LINE_LOOP, 0, size / 2);
            break;
        case 'm_grip':
            g.context.drawArrays(g.context.LINE_LOOP, 0, size / 2);
            break;
        case 'm_triph':
        case 'm_tripv':
            g.context.drawArrays(g.context.LINES, 0, size / 2);
            break;
        case ObjectTypes.line:
        case 'symline':
            g.context.drawArrays(g.context.TRIANGLES, 0, size / 2);
            break;
        case 'guide':
            g.context.drawArrays(g.context.TRIANGLES, 0, size / 2);
            break;
        case ObjectTypes.rectangle:
            g.context.drawArrays(g.context.TRIANGLES, 0, size / 2);
            break;
        case ObjectTypes.circle:
            g.context.drawArrays(g.context.TRIANGLES, 0, size / 2);
            break;
        case 'join':
            g.context.drawArrays(g.context.TRIANGLE_FAN, 0, size / 2);
            break;
        case ObjectTypes.block:
            g.context.drawArrays(g.context.TRIANGLES, 0, size / 2);
            if ((s as any).cpoints && (s as any).cpoints.length) {
                (s as any).cpoints.forEach((cp: any) => cp.isDisplayed ? drawSingle(cp) : null);
            }
            break;
        case ObjectTypes.cpoint:
            g.context.drawArrays(g.context.TRIANGLES, 0, size / 2);
            break;
        case ObjectTypes.elbowBlock:
            g.context.drawArrays(g.context.TRIANGLES, 0, size / 2);
            break;
        case ObjectTypes.tee:
            g.context.drawArrays(g.context.TRIANGLES, 0, size / 2);
            break;
        default:
            break;
    }
}

function updateActiveShapes() {
    /**
     * Фигура считается активной, если хотя бы какая-то её часть находится в области видимости
     */

    var offset = 100;

    var p1 = new Point(-offset, -offset);
    var p2 = new Point(g.canvas.width + offset, g.canvas.height + offset);

    var frame = new AbstractFrame(p1, p2, [0, 0, 0, 0]);
    frame.setPoints();

    a.shapes.forEach(shape => {
        shape.isinSelectFrameAtLeast(frame) ? shape.isinArea = true : shape.isinArea = false;
    });

    a.activeShapes = a.shapes.filter(shape => shape.isinArea).filter(shape => !shape.hidden);
}

function addShapes(shapes: unknown) {
    /**
     * Функция используется всегда, когда требуется добавление фигуры
     */
    if (Array.isArray(shapes)) {
        shapes.forEach(shape => {
            shape.saved = shape.getVertices();
            a.shapes.push(shape);
        })

    }
    else {
        const s = shapes as any;
        s.saved = s.getVertices();
        a.shapes.push(s);
    }



}

function deleteShapes() {
    /**
     * Функция используется всегда, когда требуется удаление фигуры
     */

    var selected = getSelectedShapes();

    console.log('** deleteShapes selected', selected);

    if (isOneFree(selected)) {
        /**
         * Если выбранная фигура является блоком, то выполняется операция restorePipe
         */

        let free = arrayFirst(getSelectedShapes());
        restorePipe(free);
    }
    else if (isMultipleFree(selected)) {
        selected.forEach(s => restorePipe(s));
    }

    a.shapes = a.shapes.filter(shape => !shape.isSelected);

    function isOneFree(selectedShapes: unknown[]) {
        if (selectedShapes.length === 1) {
            if ((arrayFirst(selectedShapes) as any)?.type === ObjectTypes.block) {
                return true;
            }
        }
        return false;
    }

    function isMultipleFree(selectedShapes: unknown[]) {
        if (selectedShapes.every((s: any) => s.type === ObjectTypes.block)) {
            return true;
        }
        return false;
    }

}
function deleteOneShape(selectedShape: unknown) {
    /**
     * Функция используется всегда, когда требуется удаление фигуры
     */
    const s = selectedShape as any;
    a.shapes = a.shapes.filter(shape => shape.id !== s.id);

}

// Performance optimization: Debounce route detection for pan/zoom operations
let routeDetectionTimeout: number | null = null;
const ROUTE_DETECTION_DEBOUNCE_MS = 300; // Wait 300ms after last pan/zoom before rebuilding

function scheduleRouteDetection() {
    // Clear existing timeout
    if (routeDetectionTimeout !== null) {
        clearTimeout(routeDetectionTimeout);
    }
    
    // Schedule route detection after debounce delay
    routeDetectionTimeout = setTimeout(() => {
        if (a.drawingMode === DrawingModes.duct && a.gman) {
            // Use requestAnimationFrame to ensure this runs when browser is ready
            requestAnimationFrame(() => {
                a.gman.detectRoutes(true);
            });
        }
        routeDetectionTimeout = null;
    }, ROUTE_DETECTION_DEBOUNCE_MS) as unknown as number;
}

/**
 * Cancel pending route detection
 * Call this when starting operations that depend on graph state (like selection)
 */
export function cancelPendingRouteDetection() {
    if (routeDetectionTimeout !== null) {
        clearTimeout(routeDetectionTimeout);
        routeDetectionTimeout = null;
    }
}

function updateShapesPanZoom(mode: string) {
    
    switch (mode) {
        case 'zoom':
            a.shapes.forEach(shape => {
                if (a.zl !== null) {
                    shape.zoom(a.zl);
                }
            });
            
            // Zoom is just a visual transformation - it doesn't change connectivity
            // Rebuilding graphs on zoom breaks selection functionality and is unnecessary
            // Graphs are based on point IDs and connections, which don't change with zoom

            break;
        case 'pan':
            a.shapes.forEach(shape => {
                if (a.pan_tx !== null && a.pan_ty !== null) {
                    shape.pan(a.pan_tx, a.pan_ty);
                }
            });
            
            // Pan is just a visual transformation - it doesn't change connectivity
            // No need to rebuild graphs


            break;
        default:
            break;
    }
}

function updateAndDrawShapes() {
    updateActiveShapes();
    drawShapes();
}

export { drawShapes, drawSingle, updateActiveShapes, addShapes, updateShapesPanZoom, deleteShapes, deleteOneShape, updateAndDrawShapes, drawDuctSizeLabels }
